import { CSSObject } from '@emotion/react'
import { fonts, fontWeights } from '@opallabs/pulsar'
import { colors } from '@opallabs/spectrum'
import { FlexDirections, GalleryPosition } from './positions'

const getGalleryContainer = (
  galleryContainerStyles: CSSObject,
  areAssetsHorizontallyAligned: boolean
): CSSObject => ({
  display: 'flex',
  flexDirection: areAssetsHorizontallyAligned ? 'row' : 'column',
  overflow: 'hidden',
  ...galleryContainerStyles,
})

const getImagePreviewWrapper = (
  areAssetsHorizontallyAligned: boolean
): CSSObject => ({
  display: 'flex',
  flexDirection: areAssetsHorizontallyAligned ? 'row' : 'column',
  overflow: 'hidden',
})

const getFinalImagePreview = (
  maxHeight: number,
  maxWidth: number,
  asset: GalleryPosition
): CSSObject => ({
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  height: `calc(${maxHeight}px * ${asset.height} - ${
    asset.direction === 'row' ? '0px' : '5px'
  })`,
  margin: '1px 0 0 1px',
  position: 'absolute',
  width: `calc(${maxWidth}px * ${asset.width} - ${
    asset.direction === 'row' ? '6px' : '4px'
  })`,
  zIndex: 1,
  '&:hover': {
    position: 'absolute',
  },
})

const remainingImageMarker = {
  alignItems: 'center',
  color: 'white',
  display: 'flex',
  fontFamily: fonts.sansSerif,
  fontSize: '36px',
  fontWeight: fontWeights.bold,
  height: '100%',
  justifyContent: 'center',
}

const getImgContainerStyling = (direction: FlexDirections): CSSObject => ({
  display: 'flex',
  flexDirection: direction === 'row' ? 'row' : 'column',
})

const getRemainingAssetsView = (
  maxHeight: number,
  maxWidth: number,
  asset: GalleryPosition
): CSSObject => ({
  alignItems: 'center',
  display: 'flex',
  height: `calc(${maxHeight}px * ${asset.height} - ${
    asset.direction === 'row' ? '0px' : '5px'
  })`,
  justifyContent: 'center',
  position: 'absolute',
  width: `calc(${maxWidth}px * ${asset.width} - ${
    asset.direction === 'row' ? '6px' : '4px'
  })`,
  zIndex: 1,
})

const getFinalImagePreviewWrapper = (
  maxHeight: number,
  maxWidth: number,
  asset: GalleryPosition
): CSSObject => ({
  '.kuiper-remaining-assets-view-active': {
    ...getRemainingAssetsView(maxHeight, maxWidth, asset),
    color: colors.gem.blue,
    opacity: 1,
    transition: 'opacity 0.2s ease-in',
    '.kuiper-remaining-assets-cog': {
      backgroundColor: colors.n10.base,
      borderRadius: 16,
      display: 'flex',
      height: 36,
      justifyContent: 'center',
      width: 72,
    },
  },
  '.kuiper-remaining-assets-view': {
    ...getRemainingAssetsView(maxHeight, maxWidth, asset),
    opacity: 0,
    '.kuiper-icon-wrapper': {
      opacity: 0,
    },
  },
  '&:hover': {
    '.kuiper-remaining-assets-view': {
      color: colors.n1.base,
      opacity: 1,
      transition: 'opacity 0.2s ease-in',
      '.kuiper-icon-wrapper': {
        cursor: 'pointer',
        opacity: 1,
        transition: 'opacity 0.2s ease-in',
        backgroundColor: colors.n10.base,
        borderRadius: 16,
        height: 36,
        width: 72,
      },
    },
  },
  '.kuiper-icon-wrapper:hover .kuiper-icon-cog-lg': {
    color: colors.gem.blue,
    cursor: 'pointer',
  },
})

const getPopoverStyling = (
  maxWidth: number,
  asset: GalleryPosition,
  isTooltipActive: boolean
): CSSObject => ({
  background: 'white',
  borderRadius: '4px',
  boxShadow: '0 0 8px 2px rgb(0, 0, 0, 0.6)',
  display: isTooltipActive ? 'inline-block' : 'none',
  left: 0,
  marginLeft: `calc(-220px + (${maxWidth}px * ${asset.width})/2)`,
  marginTop: '-162px',
  opacity: isTooltipActive ? 1 : 0,
  position: 'absolute',
  top: 0,
  transition: 'opacity 0.2s ease-in',
  zIndex: 1000,
  '&::after': {
    border: '10px solid white',
    borderRadius: '3px',
    content: '""',
    left: '50%',
    marginLeft: '-10px',
    position: 'absolute',
    top: '94%',
    transform: 'rotate(45deg)',
    zIndex: -1,
  },
})

const styles = {
  getFinalImagePreview,
  getFinalImagePreviewWrapper,
  getGalleryContainer,
  getImagePreviewWrapper,
  getImgContainerStyling,
  getPopoverStyling,
  remainingImageMarker,
}

export { styles }
